import { NodeType } from "../../types";
import { nodeTypeMapping } from "./constants";

// Helper to get the correct node type
export const getNodeComponentType = (nodeType: NodeType, itemType: string): string => {
  if (nodeType === "source") {
    // Map source types based on the item name
    switch (itemType) {
      case "Website":
        return "javascript-source";
      case "Mobile SDK":
        return "mobile-sdk";
      case "Server/other":
        return "server-source";
      default:
        return "javascript-source";
    }
  }
  
  return nodeTypeMapping[nodeType] || nodeType;
};

// Helper function to calculate cluster node position
export const calculateClusterNodePosition = (
  nodeType: NodeType,
  newestClusterNode: any,
  nodes: any[]
): { x: number; y: number } => {
  let x = 0;
  let y = 0;

  if (nodeType === "source" || nodeType === "load-balancer") {
    // Reduced horizontal distance from 250 to 180
    x = newestClusterNode.position.x - 180;
    
    // Count existing nodes of this type connected to the cluster
    const connectedNodesCount = nodes.filter(
      (node) =>
        node.type === nodeType &&
        node.data.targets?.includes(newestClusterNode.id),
    ).length;
    
    // Reduced vertical spacing from 150 to 80
    y = newestClusterNode.position.y + connectedNodesCount * 80;
  } else if (nodeType === "destination") {
    // Reduced horizontal distance from 250 to 180
    x = newestClusterNode.position.x + 180;
    
    // Count existing destination nodes connected to the cluster
    const connectedDestinationsCount = nodes.filter(
      (node) =>
        node.type === "destination" &&
        node.data.targets?.includes(newestClusterNode.id),
    ).length;
    
    // Reduced vertical spacing from 80 to 60
    y = newestClusterNode.position.y + connectedDestinationsCount * 60;
  }

  // Check if this position would overlap with existing nodes
  if (hasOverlap(x, y, nodes)) {
    // Try to find a nearby non-overlapping position
    const offsets = [
      { x: 0, y: 30 },
      { x: 0, y: -30 },
      { x: 30, y: 0 },
      { x: -30, y: 0 },
      { x: 30, y: 30 },
      { x: -30, y: 30 },
      { x: 30, y: -30 },
      { x: -30, y: -30 }
    ];
    
    for (const offset of offsets) {
      const newPos = {
        x: x + offset.x,
        y: y + offset.y
      };
      
      if (!hasOverlap(newPos.x, newPos.y, nodes)) {
        return newPos;
      }
    }
  }

  return { x, y };
};

// Helper function to calculate sync injector position
export const calculateSyncInjectorPosition = (
  mostRecentWebSource: any
): { x: number; y: number } => ({
  // Reduced horizontal distance from 250 to 150
  x: mostRecentWebSource.position.x - 150,
  y: mostRecentWebSource.position.y + 60, // Reduced vertical offset from 80 to 60
});

// Helper function to check if a position overlaps with existing nodes
export const hasOverlap = (x: number, y: number, nodes: any[]): boolean => {
  // Different node types have different dimensions
  // We'll use a more accurate approach based on node type
  return nodes.some(node => {
    // Get node dimensions based on type
    let nodeWidth = 200; // Default width
    let nodeHeight = 100; // Default height
    
    // Adjust dimensions based on node type
    if (node.type === "destination") {
      nodeWidth = 180;
      nodeHeight = 80;
    } else if (node.type === "cluster") {
      nodeWidth = 220;
      nodeHeight = 120;
    } else if (node.type === "source" || 
               node.type === "javascript-source" || 
               node.type === "mobile-sdk" || 
               node.type === "server-source") {
      nodeWidth = 200;
      nodeHeight = 90;
    } else if (node.type === "pipeline") {
      nodeWidth = 220;
      nodeHeight = 110;
    }
    
    // Minimum distance between nodes (reduced to allow closer placement)
    const minDistance = 10;
    
    // Calculate the center-to-center distance
    const dx = Math.abs(node.position.x - x);
    const dy = Math.abs(node.position.y - y);
    
    // Check if the nodes would overlap with the minimum distance
    // Using half-widths and half-heights for center-to-center calculation
    return dx < (nodeWidth + minDistance) / 2 && dy < (nodeHeight + minDistance) / 2;
  });
};

// Helper function to get a random position within bounds
export const getRandomPosition = (
  canvasRect: DOMRect,
  viewport: any
): { x: number; y: number } => {
  // Calculate a position in the center of the current viewport
  const viewportCenter = {
    x: canvasRect.width / 2,
    y: canvasRect.height / 2
  };
  
  // Apply viewport transform to get the actual position in the canvas
  const transformedPosition = viewport ? {
    x: (viewportCenter.x - viewport.x) / viewport.zoom,
    y: (viewportCenter.y - viewport.y) / viewport.zoom
  } : viewportCenter;
  
  // Add minimal randomness to keep nodes closer together
  const randomOffset = {
    x: (Math.random() * 40 - 20), // Further reduced range for x (from 100 to 40)
    y: (Math.random() * 40 - 20)  // Further reduced range for y (from 100 to 40)
  };
  
  return {
    x: transformedPosition.x + randomOffset.x,
    y: transformedPosition.y + randomOffset.y
  };
};

// Constants for node placement
const GRID_SPACING = 40; // Further reduced spacing between nodes for closer proximity

// Helper function to find a non-overlapping position
export const findNonOverlappingPosition = (
  canvasRect: DOMRect,
  viewport: any,
  nodes: any[]
): { x: number; y: number } => {
  // If there are no nodes, place in the center of the viewport
  if (nodes.length === 0) {
    const viewportCenter = {
      x: canvasRect.width / 2,
      y: canvasRect.height / 2
    };
    
    // Apply viewport transform to get the actual position in the canvas
    return viewport ? {
      x: (viewportCenter.x - viewport.x) / viewport.zoom,
      y: (viewportCenter.y - viewport.y) / viewport.zoom
    } : viewportCenter;
  }
  
  // Find the most recently added node (assuming higher IDs are more recent)
  const mostRecentNode = nodes.reduce((latest, node) => {
    // Extract timestamp from node ID (assuming format like 'type-name-timestamp')
    const latestTimestamp = parseInt(latest.id.split('-').pop() || '0', 10);
    const currentTimestamp = parseInt(node.id.split('-').pop() || '0', 10);
    return currentTimestamp > latestTimestamp ? node : latest;
  }, nodes[0]);
  
  // Get the viewport center
  const viewportCenter = {
    x: canvasRect.width / 2,
    y: canvasRect.height / 2
  };
  
  // Apply viewport transform to get the actual center position in the canvas
  const center = viewport ? {
    x: (viewportCenter.x - viewport.x) / viewport.zoom,
    y: (viewportCenter.y - viewport.y) / viewport.zoom
  } : viewportCenter;
  
  // Determine the placement area - use a compact area near the most recent node
  // or near the center if that's more appropriate
  const placementCenter = mostRecentNode ? mostRecentNode.position : center;
  
  // Create a spiral pattern of positions around the placement center
  // This will create a more compact and natural-looking arrangement
  const positions = [];
  
  // First try positions very close to the placement center
  const closePositions = [
    { x: placementCenter.x + GRID_SPACING, y: placementCenter.y }, // Right
    { x: placementCenter.x, y: placementCenter.y + GRID_SPACING }, // Below
    { x: placementCenter.x - GRID_SPACING, y: placementCenter.y }, // Left
    { x: placementCenter.x, y: placementCenter.y - GRID_SPACING }, // Above
    { x: placementCenter.x + GRID_SPACING, y: placementCenter.y + GRID_SPACING }, // Diagonal bottom-right
    { x: placementCenter.x - GRID_SPACING, y: placementCenter.y + GRID_SPACING }, // Diagonal bottom-left
    { x: placementCenter.x - GRID_SPACING, y: placementCenter.y - GRID_SPACING }, // Diagonal top-left
    { x: placementCenter.x + GRID_SPACING, y: placementCenter.y - GRID_SPACING }, // Diagonal top-right
  ];
  
  positions.push(...closePositions);
  
  // Then add positions in a spiral pattern
  const maxSpirals = 4; // Number of spiral loops to try
  const spiralPoints = 8; // Points per spiral
  
  for (let spiral = 1; spiral <= maxSpirals; spiral++) {
    const radius = GRID_SPACING * spiral;
    
    for (let i = 0; i < spiralPoints; i++) {
      const angle = (i / spiralPoints) * 2 * Math.PI;
      positions.push({
        x: placementCenter.x + Math.cos(angle) * radius,
        y: placementCenter.y + Math.sin(angle) * radius
      });
    }
  }
  
  // Try each position in order (closest to furthest)
  for (const pos of positions) {
    if (!hasOverlap(pos.x, pos.y, nodes)) {
      return pos;
    }
  }
  
  // If no position works in the spiral, try a more compact grid approach
  const smallGridSpacing = GRID_SPACING / 2; // Even smaller grid for tighter packing
  const maxGridSize = 6; // Try up to a 6x6 grid
  
  for (let gridSize = 1; gridSize <= maxGridSize; gridSize++) {
    // Create a grid of positions of increasing size
    for (let i = -gridSize; i <= gridSize; i++) {
      for (let j = -gridSize; j <= gridSize; j++) {
        // Skip positions we've already tried
        if (Math.abs(i) < gridSize && Math.abs(j) < gridSize) {
          continue;
        }
        
        const pos = {
          x: placementCenter.x + i * smallGridSpacing,
          y: placementCenter.y + j * smallGridSpacing
        };
        
        if (!hasOverlap(pos.x, pos.y, nodes)) {
          return pos;
        }
      }
    }
  }
  
  // If all else fails, try to find any position by slightly offsetting from existing nodes
  for (const node of nodes) {
    const offsets = [
      { x: 30, y: 0 },
      { x: 0, y: 30 },
      { x: 30, y: 30 },
      { x: -30, y: 0 },
      { x: 0, y: -30 },
      { x: -30, y: -30 },
      { x: 30, y: -30 },
      { x: -30, y: 30 }
    ];
    
    for (const offset of offsets) {
      const pos = {
        x: node.position.x + offset.x,
        y: node.position.y + offset.y
      };
      
      if (!hasOverlap(pos.x, pos.y, nodes)) {
        return pos;
      }
    }
  }
  
  // Last resort: place at the center with a tiny random offset
  return {
    x: center.x + (Math.random() * 20 - 10),
    y: center.y + (Math.random() * 20 - 10)
  };
};
